// QR Kiosk v2 — Settings (surface 5). v1 parity, synchronous (no API, no
// skeleton). Language rows (normalized split('-')[0] match + try/catch around
// changeLanguage — bug #14), reload note, company-details nav row (Pass 1b
// 2026-07-24, §1b.6 — replaces the removed dashboard tile; opens CompanyV2),
// account card with v1 sign-out verbatim. Tokens only.
import React, { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import useTranslation, { SupportedLanguage } from "../../../shared/hooks/useTranslation";
import { KPage } from "../shared/KPage";
import { Glyph, GD } from "../shared/glyphs";
import { ErrorCallout } from "../shared/ErrorCallout";
import { usePressed } from "../shared/usePressed";
import accountContext from "../../../account/accountContext";
import { refreshAccount } from "../../../account/accountSlice";
import { buildAccountSignInWithReturn } from "../../../account/routes";

// Native display names — deliberately NEVER translated (parity, §7.1).
const LANG_NAMES: Record<SupportedLanguage, string> = {
  sk: "Slovenčina",
  en: "English",
  cs: "Čeština",
};

const cardBase: React.CSSProperties = {
  border: "1px solid var(--border)",
  borderRadius: 12,
  boxShadow: "var(--shadow)",
  background: "var(--card)",
  overflow: "hidden",
};

function LangRow({
  lng,
  name,
  index,
  active,
  onSelect,
}: {
  lng: SupportedLanguage;
  name: string;
  index: number;
  active: boolean;
  onSelect: (lng: SupportedLanguage) => void;
}) {
  const press = usePressed();
  return (
    <button
      type="button"
      {...press.pressHandlers}
      onClick={() => onSelect(lng)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        width: "100%",
        height: 56,
        padding: "0 14px",
        border: 0,
        fontFamily: "inherit",
        cursor: "pointer",
        borderTop: index ? "1px solid var(--border-soft)" : "none",
        background: active ? "var(--tier-accent-soft)" : "transparent",
        boxShadow: active ? "inset 2px 0 0 var(--tier-accent)" : "none",
        ...press.pressStyle,
      }}
    >
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "var(--muted)",
          width: 24,
          flexShrink: 0,
        }}
      >
        {lng}
      </span>
      <span style={{ flex: 1, fontSize: 15, fontWeight: active ? 700 : 500, color: "var(--ink)", textAlign: "left" }}>
        {name}
      </span>
      {active && (
        <span style={{ color: "var(--tier-accent)", display: "inline-flex", flexShrink: 0 }}>
          <Glyph d={GD.check} size={18} />
        </span>
      )}
    </button>
  );
}

export function SettingsV2() {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n, changeLanguage, getSupportedLanguages, translate } = useTranslation("public");
  const user = useAppSelector((s) => s.account.user);
  const signOutPress = usePressed();
  const companyPress = usePressed();

  const [changeFailed, setChangeFailed] = useState(false);
  const currentLang = i18n.language.split("-")[0]; // normalized — bug #14 fix (en-US → en)

  const handleSelect = async (lng: SupportedLanguage) => {
    setChangeFailed(false);
    if (lng === currentLang) return; // normalized compare — regional variants no longer dodge the no-op
    try {
      await changeLanguage(lng); // persists i18nextLng (client-only, parity)
      window.location.reload(); // FULL hard reload — re-resolves every cached string
    } catch {
      setChangeFailed(true); // v1 left this await unhandled → silent stall; v2 surfaces it
    }
  };

  const firstName = user?.firstName || "";
  const lastName = user?.lastName || "";
  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  const fullName = `${firstName} ${lastName}`.trim();
  const role = (user?.role || "").toLowerCase(); // role only, no company name (KV2-0-a)

  // v1 logout VERBATIM (AccountSheet.handleSignOut) — sign in with a return URL.
  const handleSignOut = () => {
    const currentUrl = `${location.pathname}${location.search}${location.hash}`;
    accountContext.logout();
    dispatch(refreshAccount(null));
    navigate(buildAccountSignInWithReturn(currentUrl));
  };

  return (
    <KPage titleKey="kioskSettings" glyph={GD.gear} machineNumber={id}>
      {changeFailed && <ErrorCallout title={translate("kioskV2LanguageChangeFailed")} />}

      {/* Language section */}
      <div className="eyebrow" style={{ color: "var(--muted)", padding: "12px 18px 6px" }}>
        {translate("kioskLanguage")}
      </div>
      <div className="kv2-enter" style={{ ...cardBase, margin: "0 14px" }}>
        {getSupportedLanguages().map((lng, i) => (
          <LangRow
            key={lng}
            lng={lng}
            name={LANG_NAMES[lng]}
            index={i}
            active={lng === currentLang}
            onSelect={handleSelect}
          />
        ))}
      </div>
      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 9.5,
          color: "var(--faint)",
          padding: "9px 18px 0",
          letterSpacing: "0.03em",
        }}
      >
        {translate("kioskV2LanguageReloadNote")}
      </div>

      {/* Company details — nav row to CompanyV2 (§1b.6; replaces the dashboard tile).
          §3.5 nav guard — never emit /public-v2/machines/undefined/… */}
      <div className="kv2-enter" style={{ ...cardBase, margin: "16px 14px 0", animationDelay: "0.03s" }}>
        <button
          type="button"
          {...companyPress.pressHandlers}
          onClick={() => {
            if (id) navigate(`/public-v2/machines/${id}/company`);
          }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            width: "100%",
            height: 56,
            padding: "0 14px",
            border: 0,
            background: "transparent",
            fontFamily: "inherit",
            cursor: "pointer",
            ...companyPress.pressStyle,
          }}
        >
          <span style={{ color: "var(--accent)", display: "inline-flex", flexShrink: 0 }}>
            <Glyph d={GD.bldg} size={20} />
          </span>
          <span style={{ flex: 1, fontSize: 15, fontWeight: 600, color: "var(--ink)", textAlign: "left" }}>
            {translate("kioskCompanyDetails")}
          </span>
          <span style={{ color: "var(--muted)", display: "inline-flex", flexShrink: 0 }}>
            <Glyph d={GD.chev} size={18} />
          </span>
        </button>
      </div>

      {/* Account section */}
      <div style={{ margin: "16px 14px 0" }}>
        <div className="eyebrow" style={{ color: "var(--muted)", padding: "0 4px 6px" }}>
          {translate("kioskV2Account")}
        </div>
        <div className="kv2-enter" style={{ ...cardBase, animationDelay: "0.05s" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 11, height: 56, padding: "0 14px" }}>
            <span
              style={{
                width: 28,
                height: 28,
                borderRadius: 999,
                background: "var(--tier-accent)",
                color: "var(--tier-accent-fg)",
                display: "inline-grid",
                placeItems: "center",
                fontSize: 10,
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {initials}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "var(--ink)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {fullName}
              </div>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 9.5, color: "var(--faint)" }}>{role}</div>
            </div>
            <button
              type="button"
              {...signOutPress.pressHandlers}
              onClick={handleSignOut}
              style={{
                height: 36,
                padding: "0 14px",
                borderRadius: 9,
                border: "1px solid var(--border)",
                background: "var(--bg)",
                color: "color-mix(in oklab, var(--red) 75%, var(--ink))",
                fontFamily: "inherit",
                fontSize: 12.5,
                fontWeight: 600,
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                cursor: "pointer",
                flexShrink: 0,
                ...signOutPress.pressStyle,
              }}
            >
              <Glyph d={GD.out} size={15} />
              {translate("kioskLogout")}
            </button>
          </div>
        </div>
      </div>
    </KPage>
  );
}

export default SettingsV2;
